/**
 * Embed session store (EMB-09)
 *
 * The embed shell's token lives ONLY in this module-scoped variable —
 * deliberately never `localStorage`/`sessionStorage` (design.md: "Token
 * storage in the browser: Module-scoped variable — survives no reload, is
 * not readable from storage, dies with the tab"). `apiFetch` (lib/api.js)
 * and `SocketContext` (T11) read it through the getters below; `EmbedContext`
 * (T12) is the only writer, set once at bootstrap and again on renewal.
 * `AuthContext` (T13) reads `getEmbedAuthHeaders()` for the two bootstrap
 * calls that bypass `apiFetch` (`/api/auth/me`, `/api/plans/account/features`).
 */
import { EMBED_MARKER_HEADER } from "@touchworks/shared/platform/embedProtocol";

let _token = null;
let _expiresAt = null;

/**
 * @param {string} token
 * @param {string|null} [expiresAt] - ISO 8601, mirrors POST /api/v1/embed/sessions
 */
export function setEmbedToken(token, expiresAt = null) {
  _token = token;
  _expiresAt = expiresAt;
}

/** @returns {string|null} */
export function getEmbedToken() {
  return _token;
}

/** @returns {string|null} */
export function getEmbedTokenExpiresAt() {
  return _expiresAt;
}

/** Chamado no logout/timeout de renovação — nunca deixa um token velho pairando. */
export function clearEmbedToken() {
  _token = null;
  _expiresAt = null;
}

/** @returns {boolean} true quando existe um token embutido em memória agora. */
export function isEmbedSessionActive() {
  return Boolean(_token);
}

/**
 * Headers para acrescentar a um `fetch()` cru (fora do choke point de
 * `apiFetch`) quando agindo como o shell embutido — mesmo par
 * Authorization/marcador que `apiFetch` já injeta (lib/api.js). Sem token,
 * retorna `{}`: mesclar num objeto de headers não muda nada.
 * @returns {Record<string,string>}
 */
export function getEmbedAuthHeaders() {
  if (!_token) return {};
  return { Authorization: `Bearer ${_token}`, [EMBED_MARKER_HEADER]: "1" };
}
